// Customer lookup (migration 0135, Umer 2026-10-17). The Customers page is the owner's: the full list.
// Anyone else the owner switches on (Admin Panel → "Customer lookup") gets the same page in search-only
// mode — empty until they type, at most CUSTOMER_LOOKUP_LIMIT matches, no paging and no export. Nobody
// else sees the page; the route sends them home (components/CustomersRoute.tsx).
import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { UserRole, type User } from '../types';
import { isOwner } from './roles';

/** Search-only mode: matches shown per search, and the characters typed before a search runs. */
export const CUSTOMER_LOOKUP_LIMIT = 10;
export const CUSTOMER_LOOKUP_MIN_CHARS = 2;

/** The roles the owner may switch on — the ones the customers table lets read (is_dispatch_tier). */
export const CUSTOMER_LOOKUP_GRANTABLE: UserRole[] = [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER];

const accessCache = new Map<string, Promise<boolean>>();

/** May this user open the Customers page at all? The owner always; anyone else only when switched on. */
export function hasCustomerLookup(user: Pick<User, 'id' | 'role'> | null | undefined): Promise<boolean> {
  if (!user) return Promise.resolve(false);
  if (isOwner(user.role)) return Promise.resolve(true);
  if (!CUSTOMER_LOOKUP_GRANTABLE.includes(user.role)) return Promise.resolve(false);
  let p = accessCache.get(user.id);
  if (!p) {
    p = Promise.resolve(supabase().rpc('has_customer_lookup')).then(({ data, error }: any) => !error && data === true, () => false);
    accessCache.set(user.id, p);
  }
  return p;
}

/** React: whether the signed-in user may open Customers — null while it is being asked. */
export function useCustomerLookup(user: Pick<User, 'id' | 'role'> | null | undefined): boolean | null {
  const [ok, setOk] = useState<boolean | null>(isOwner(user?.role) ? true : null);
  useEffect(() => {
    let live = true;
    hasCustomerLookup(user).then((v) => live && setOk(v));
    return () => { live = false; };
  }, [user?.id, user?.role]);
  return ok;
}

/** Test seam. */
export function _resetCustomerLookupCache(): void { accessCache.clear(); }

export interface CustomerLookupRow {
  userId: string;
  grantedByName: string | null;
  grantedAt: number;
}

export async function fetchCustomerLookup(): Promise<CustomerLookupRow[]> {
  const { data, error } = await supabase()
    .from('customer_lookup')
    .select('user_id,granted_by_name,granted_at')
    .order('granted_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r: any) => ({ userId: r.user_id, grantedByName: r.granted_by_name ?? null, grantedAt: Date.parse(r.granted_at) }));
}

export async function grantCustomerLookup(userId: string): Promise<void> {
  const { error } = await supabase().rpc('customer_lookup_grant', { p_user: userId });
  if (error) throw error;
  accessCache.delete(userId);
}

export async function revokeCustomerLookup(userId: string): Promise<void> {
  const { error } = await supabase().rpc('customer_lookup_revoke', { p_user: userId });
  if (error) throw error;
  accessCache.delete(userId);
}
